
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupLabel,
} from "@/components/ui/sidebar";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import AdminMenu from "./AdminMenu";
import UserMenu from "./UserMenu";
import { useToast } from "@/components/ui/use-toast";
import { useLocation } from "react-router-dom";
import { AppRole } from "@/types/database/auth";

interface DashboardSidebarProps {
  children?: React.ReactNode;
}

export function DashboardSidebar({ children }: DashboardSidebarProps) {
  const [userRole, setUserRole] = useState<AppRole | null>(null);
  const { toast } = useToast();
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');
  const locationState = location.state as { role?: AppRole } | null;
  
  useEffect(() => {
    const checkUserRole = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!session) {
          setUserRole(null);
          return;
        }

        const { data: roleData, error } = await supabase
          .from("user_roles")
          .select("role")
          .eq("user_id", session.user.id)
          .maybeSingle();

        if (error) {
          console.error("Error fetching user role:", error);
          toast({
            variant: "destructive",
            title: "Error",
            description: "Failed to fetch user role. Please try again later.",
          });
          setUserRole(null);
          return;
        }

        // Set the user role
        setUserRole(roleData?.role as AppRole || null);
      } catch (error: any) {
        console.error("Error in checkUserRole:", error);
        toast({
          variant: "destructive",
          title: "Error",
          description: "An unexpected error occurred. Please try again later.",
        });
        setUserRole(null);
      }
    };

    checkUserRole();
  }, [toast, location.pathname]);

  // Determine if user has admin access (either admin or producer)
  const hasAdminAccess = userRole === 'admin' || userRole === 'producer';
  
  // For debugging
  console.log("User role:", userRole);
  console.log("Is admin route:", isAdminRoute);
  console.log("Has admin access:", hasAdminAccess);

  return (
    <Sidebar variant="inset" collapsible="icon" className="hidden md:block">
      <SidebarContent className="pt-24 md:pt-32">
        <SidebarGroup>
          <SidebarGroupLabel>
            {isAdminRoute ? "Admin Dashboard" : "Dashboard"}
          </SidebarGroupLabel>
          {children || (
            isAdminRoute && hasAdminAccess ? <AdminMenu /> : <UserMenu />
          )}
        </SidebarGroup>
      </SidebarContent>
    </Sidebar>
  );
}
